// src/components/pages/EventManager/organisms/EventForm.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, LinearProgress, IconButton, Button } from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  collection,
  serverTimestamp,
  doc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, auth } from "../../../../firebase";

import CustomCard from "../../../molecules/ChallengeCreator/CustomCard";
import InputField from "../atoms/InputField";
import DateFields from "../molecules/DateFields";
import CustomButton from "../atoms/CustomButton";
import ErrorMessage from "../atoms/ErrorMessage";

const storage = getStorage();

/**
 * EventForm
 * - mode: 'create' | 'edit'
 * - initialEvent: when editing, pass the full event object { id, title, ... }
 * - showCard: when false, renders the inner form without the outer CustomCard (useful inside dialogs)
 */
const EventForm = ({
  theme,
  onSuccessfulSubmit,
  mode = "create",
  initialEvent = null,
  showCard = true,
}) => {
  // Text/date/number fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState(""); // "YYYY-MM-DD"
  const [endDate, setEndDate] = useState(""); // "YYYY-MM-DD"
  const [price, setPrice] = useState("");
  const [rentalDurationDays, setRentalDurationDays] = useState("7");

  // Thumbnail (optional)
  const [thumbFile, setThumbFile] = useState(null);
  const [thumbProgress, setThumbProgress] = useState(0);
  const [thumbError, setThumbError] = useState("");
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const thumbInputRef = useRef(null);

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Prefill when editing
  useEffect(() => {
    if (mode === "edit" && initialEvent) {
      setTitle(initialEvent.title || "");
      setDescription(initialEvent.description || "");
      const toYmd = (d) => {
        const date =
          d?.toDate?.() ??
          (d instanceof Date ? d : d ? new Date(d) : null);
        if (!date) return "";
        return date.toISOString().split("T")[0];
      };
      setStartDate(toYmd(initialEvent.startDate));
      setEndDate(toYmd(initialEvent.endDate));
      setPrice(
        typeof initialEvent.price === "number"
          ? String(initialEvent.price)
          : initialEvent.price || ""
      );
      setRentalDurationDays(
        Number.isFinite(initialEvent.rentalDurationDays)
          ? String(initialEvent.rentalDurationDays)
          : "7"
      );
    }
  }, [mode, initialEvent]);

  const isEdit = useMemo(() => mode === "edit", [mode]);

  // ---------- upload helpers ----------
  const delay = (ms) => new Promise((res) => setTimeout(res, ms));
  const isRetryableStorageError = (err) => {
    const code = err?.code || "";
    if (!code) return true;
    return (
      code === "storage/retry-limit-exceeded" ||
      code === "storage/unknown" ||
      code === "storage/quota-exceeded" ||
      code === "storage/unauthenticated"
    );
  };
  async function uploadWithRetry(storagePath, file, onProgress, setErr, maxRetries = 2) {
    let attempt = 0;
    let lastErr = null;
    while (attempt <= maxRetries) {
      try {
        const result = await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(ref(storage, storagePath), file, {
            contentType: file.type,
          });
          task.on(
            "state_changed",
            (snap) => {
              const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
              onProgress?.(pct);
            },
            (err) => reject(err),
            async () => {
              try {
                const url = await getDownloadURL(task.snapshot.ref);
                resolve({
                  url,
                  path: storagePath,
                  contentType: file.type,
                  size: file.size,
                  updatedAt: Date.now(),
                });
              } catch (e) {
                reject(e);
              }
            }
          );
        });
        setErr("");
        return result;
      } catch (err) {
        lastErr = err;
        if (attempt === maxRetries || !isRetryableStorageError(err)) {
          setErr(
            `Upload failed${attempt ? ` after ${attempt + 1} attempts` : ""}. ${
              err?.message || "Unknown error"
            }`
          );
          break;
        }
        const backoff = 500 + attempt * 1000;
        await delay(backoff);
        attempt += 1;
      }
    }
    throw lastErr;
  }
  const ext = (name) => {
    const i = name.lastIndexOf(".");
    return i >= 0 ? name.substring(i) : "";
  };
  const validImage = (f) => !!f && /^image\//.test(f.type);

  const fileInfo = (f) =>
    f ? `${f.name} (${Math.round(f.size / 1024 / 1024)} MB)` : "None selected";

  const pickThumb = () => thumbInputRef.current?.click();
  const onThumbChange = (e) => {
    const f = e.target.files?.[0] || null;
    setThumbError("");
    setThumbProgress(0);
    if (f && !validImage(f)) {
      setThumbError("Please select a valid image file.");
      setThumbFile(null);
      return;
    }
    setThumbFile(f);
  };
  const clearThumb = () => {
    setThumbFile(null);
    setThumbError("");
    setThumbProgress(0);
    if (thumbInputRef.current) thumbInputRef.current.value = "";
  };
  const retryThumb = async () => {
    if (!thumbFile || !validImage(thumbFile)) return;
    setThumbError("");
    try {
      setSaving(true);
      setUploadingThumb(true);
      const tmpPath = `events/_tmp/thumb-retry-${Date.now()}${ext(thumbFile.name)}`;
      await uploadWithRetry(tmpPath, thumbFile, setThumbProgress, setThumbError, 2);
    } catch (e) {
      /* error already surfaced */
    } finally {
      setUploadingThumb(false);
      setSaving(false);
    }
  };

  // ---------- submit ----------
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError("");

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError("No authenticated user found.");
      return;
    }

    // Date validations
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (!title?.trim()) return setError("Please provide a title for the event.");
    if (!description?.trim()) return setError("Please provide a description for the event.");
    if (!startDate) return setError("Please select a start date for the event.");
    if (!endDate) return setError("Please select an end date for the event.");

    const [sy, sm, sd] = startDate.split("-").map(Number);
    const [ey, em, ed] = endDate.split("-").map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);

    if (start < today) return setError("The start date cannot be before today.");
    if (end <= start) return setError("The end date must be after the start date.");
    if (end - start < 24 * 60 * 60 * 1000) {
      return setError("The end date must be at least one day after the start date.");
    }

    if (!price && price !== 0) return setError("Please provide a purchase price for the event.");
    const priceNum = Number(price);
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      return setError("The purchase price must be a valid non-negative number.");
    }

    const durationInt = Number(rentalDurationDays);
    if (!Number.isInteger(durationInt) || durationInt <= 0) {
      return setError("Rental duration must be a positive whole number of days.");
    }

    try {
      setSaving(true);

      if (isEdit && initialEvent?.id) {
        const refDoc = doc(db, "events", initialEvent.id);

        // Upload new thumbnail first (if provided)
        let newThumbMeta = null;
        if (thumbFile) {
          setUploadingThumb(true);
          const path = `events/${initialEvent.id}/thumbnail/${Date.now()}${ext(thumbFile.name)}`;
          newThumbMeta = await uploadWithRetry(path, thumbFile, setThumbProgress, setThumbError, 2);
          setUploadingThumb(false);
        }

        await updateDoc(refDoc, {
          title: title.trim(),
          description: description.trim(),
          startDate: start,
          endDate: end,
          price: priceNum,
          rentalDurationDays: durationInt,
          ...(newThumbMeta ? { thumbnail: newThumbMeta } : {}),
          updatedAt: serverTimestamp(),
        });

        // Best-effort delete of old thumbnail if replaced
        if (newThumbMeta && initialEvent?.thumbnail?.path) {
          const oldPath = initialEvent.thumbnail.path;
          if (oldPath && oldPath !== newThumbMeta.path) {
            try {
              await deleteObject(ref(storage, oldPath));
            } catch (delErr) {
              console.warn("[EventForm] Could not delete old thumbnail:", oldPath, delErr?.code || delErr?.message);
            }
          }
        }
      } else {
        // Create: pre-generate ID, upload thumb (if any), then setDoc
        const refDoc = doc(collection(db, "events"));

        let thumbnailMeta = null;
        if (thumbFile) {
          setUploadingThumb(true);
          const path = `events/${refDoc.id}/thumbnail/${Date.now()}${ext(thumbFile.name)}`;
          thumbnailMeta = await uploadWithRetry(path, thumbFile, setThumbProgress, setThumbError, 2);
          setUploadingThumb(false);
        }

        await setDoc(refDoc, {
          title: title.trim(),
          description: description.trim(),
          startDate: start,
          endDate: end,
          price: priceNum,
          rentalDurationDays: durationInt,
          ...(thumbnailMeta ? { thumbnail: thumbnailMeta } : {}),
          createdAt: serverTimestamp(),
          creatorUid: currentUser.uid,
        });
      }

      // Reset form after success
      setTitle("");
      setDescription("");
      setStartDate("");
      setEndDate("");
      setPrice("");
      setRentalDurationDays("7");
      setThumbFile(null);
      setThumbError("");
      setThumbProgress(0);
      setError("");

      onSuccessfulSubmit && onSuccessfulSubmit();
    } catch (err) {
      console.error("[EventForm] submit error:", err);
      setError(
        `Error ${isEdit ? "updating" : "creating"} event: ${err?.message || "unknown error"}`
      );
    } finally {
      setSaving(false);
      setUploadingThumb(false);
    }
  };

  // Inner form content
  const FormInner = (
    // Keep onSubmit to allow pressing Enter in inputs
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <InputField
          label="Title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
        <InputField
          label="Description"
          type="textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
        />

        {/* Event Thumbnail (optional) */}
        <Box
          sx={{
            p: 2,
            borderRadius: "12px",
            border: `1px dashed ${theme.colors.palePurple}`,
            backgroundColor: theme.colors.mediumPurple,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: theme.colors.amber, fontFamily: "basic-sans", mb: 1 }}
          >
            Event Thumbnail (optional)
          </Typography>

          <input
            ref={thumbInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={onThumbChange}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CustomButton
              onClick={pickThumb}
              theme={theme}
              disabled={saving}
              sx={{
                backgroundColor: theme.colors.darkBlue,
                "&:hover": { backgroundColor: theme.colors.amber },
              }}
            >
              Choose File
            </CustomButton>

            {thumbFile && (
              <IconButton
                aria-label="clear thumbnail"
                onClick={clearThumb}
                size="small"
                disabled={saving}
                sx={{ color: theme.colors.amber }}
              >
                <HighlightOffIcon fontSize="small" />
              </IconButton>
            )}

            {thumbError && (
              <Button
                size="small"
                startIcon={<ReplayIcon />}
                onClick={retryThumb}
                disabled={saving || !thumbFile}
                sx={{ textTransform: "none", ml: 1 }}
              >
                Retry upload
              </Button>
            )}
          </Box>

          <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
            {thumbFile ? fileInfo(thumbFile) : "None selected"}
          </Typography>

          {(uploadingThumb && thumbFile) && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress variant="determinate" value={thumbProgress} />
              <Typography variant="caption">{thumbProgress}%</Typography>
            </Box>
          )}

          {thumbError && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
              {thumbError}
            </Typography>
          )}
        </Box>

        <DateFields
          startDate={startDate}
          endDate={endDate}
          onStartChange={(e) => setStartDate(e.target.value)}
          onEndChange={(e) => setEndDate(e.target.value)}
        />

        {/* SIDE-BY-SIDE ROW: Purchase Price + Rental Days */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
            width: "100%",
          }}
        >
          <InputField
            label="Purchase Price"
            type="text"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
            inputMode="numeric"
            pattern="[0-9]*"
            allowAllCharacters={false}
          />

          <InputField
            label="Rental Duration (days)"
            type="number"
            value={rentalDurationDays}
            onChange={(e) => setRentalDurationDays(e.target.value)}
            required
            inputMode="numeric"
            min="1"
            step="1"
          />
        </Box>

        {/* Explicit click handler to avoid relying on CustomButton's internal 'type' */}
        <CustomButton
          type="button"
          onClick={handleSubmit}
          theme={theme}
          disabled={saving}
          sx={{
            backgroundColor: theme.colors.green,
            marginTop: "18px",
            borderRadius: "5px",
            "&:hover": { backgroundColor: theme.colors.blue },
          }}
        >
          {isEdit ? (saving ? "Updating…" : "Update") : (saving ? "Creating…" : "Create")}
        </CustomButton>
      </Box>
      <ErrorMessage message={error} />
    </Box>
  );

  if (!showCard) {
    return FormInner;
  }

  return <CustomCard title={isEdit ? "Edit Event" : "New Event Form"}>{FormInner}</CustomCard>;
};

EventForm.propTypes = {
  theme: PropTypes.object.isRequired,
  onSuccessfulSubmit: PropTypes.func,
  mode: PropTypes.oneOf(["create", "edit"]),
  initialEvent: PropTypes.object,
  showCard: PropTypes.bool,
};

export default EventForm;
